import React, { useState, useRef, useEffect } from 'react';
import { Search, User, ChevronDown, MapPin, Globe } from 'lucide-react';

const Header = ({ onSearch }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const searchTimeoutRef = useRef(null);
  const searchContainerRef = useRef(null);

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowResults(false);
        setShowMobileSearch(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const searchNominatim = async (query) => {
    if (!query || query.trim().length < 3) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&addressdetails=1`
      );
      const data = await response.json();
      setSearchResults(data);
      setShowResults(true);
    } catch (error) {
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for debouncing
    searchTimeoutRef.current = setTimeout(() => {
      searchNominatim(query);
    }, 500);
  };

  const handleResultClick = (result) => {
    const location = {
      lat: parseFloat(result.lat),
      lon: parseFloat(result.lon),
      name: result.display_name,
      boundingbox: result.boundingbox,
    };

    onSearch && onSearch(location);
    setSearchQuery(result.display_name);
    setShowResults(false);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchResults.length > 0) {
      handleResultClick(searchResults[0]);
    }
  };

  return (
    <div className="bg-slate-800 text-white px-3 md:px-4 py-2 md:py-3 flex items-center justify-between shadow-lg relative z-30">
      {/* Logo and Title - hide when mobile search is open */}
      {!showMobileSearch && (
        <div className="flex items-center space-x-2 md:space-x-4">
          <div className="bg-blue-600 p-1.5 md:p-2 rounded-lg">
            <Globe className="w-5 h-5 md:w-6 md:h-6 text-white" />
          </div>
          <div className="hidden sm:block">
            <h1 className="text-lg md:text-xl font-bold">Panaji GeoPortal</h1>
            <p className="text-xs md:text-sm text-gray-300">Dev. by BODHI3 Consulting</p>
          </div>
          <div className="sm:hidden">
            <h1 className="text-sm font-bold">Panaji GeoPortal</h1>
            <p className="text-xs text-gray-300">Dev. by BODHI3 Consulting</p>
          </div>
        </div>
      )}

      {/* Desktop Search - Always visible */}
      <div className="hidden md:block flex-1 max-w-xs md:max-w-md mx-2 md:mx-8 relative" ref={searchContainerRef}>
        <form onSubmit={handleSearchSubmit}>
          <div className="relative">
            <Search className="absolute left-2 md:left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3 md:w-4 md:h-4" />
            <input
              type="text"
              placeholder="Search location..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="w-full bg-gray-700 text-white pl-7 md:pl-10 pr-2 md:pr-4 py-1.5 md:py-2 text-sm md:text-base rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {isSearching && (
              <div className="absolute right-2 md:right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin rounded-full h-3 w-3 md:h-4 md:w-4 border-b-2 border-white"></div>
              </div>
            )}
          </div>
        </form>

        {/* Search Results Dropdown */}
        {showResults && searchResults.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white text-gray-900 rounded-lg shadow-lg max-h-64 overflow-y-auto z-50">
            {searchResults.map((result, index) => (
              <button
                key={index}
                onClick={() => handleResultClick(result)}
                className="w-full text-left px-3 py-2 hover:bg-gray-100 border-b border-gray-200 last:border-b-0 flex items-start space-x-2"
              >
                <MapPin className="w-4 h-4 mt-1 flex-shrink-0 text-blue-600" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{result.display_name.split(',')[0]}</div>
                  <div className="text-xs text-gray-500 truncate">{result.display_name}</div>
                </div>
              </button>
            ))}
          </div>
        )}

        {showResults && searchQuery && searchResults.length === 0 && !isSearching && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white text-gray-900 rounded-lg shadow-lg p-3 z-50">
            <p className="text-sm text-gray-500">No results found</p>
          </div>
        )}
      </div>

      {/* Mobile Search - Expandable in same row */}
      <div className={`md:hidden flex items-center space-x-2 ${showMobileSearch ? 'flex-1' : ''}`}>
        {showMobileSearch ? (
          <div className="flex-1 relative" ref={searchContainerRef}>
            <form onSubmit={handleSearchSubmit} className="flex items-center space-x-2">
              <button
                type="button"
                onClick={() => setShowMobileSearch(false)}
                className="text-gray-400 hover:text-white p-2"
              >
                <ChevronDown className="w-5 h-5 rotate-90" />
              </button>
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search location..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  autoFocus
                  className="w-full bg-gray-700 text-white pl-10 pr-4 py-2 text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {isSearching && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  </div>
                )}
              </div>
            </form>

            {/* Mobile Search Results Dropdown */}
            {showResults && searchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white text-gray-900 rounded-lg shadow-lg max-h-64 overflow-y-auto z-50">
                {searchResults.map((result, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      handleResultClick(result);
                      setShowMobileSearch(false);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100 border-b border-gray-200 last:border-b-0 flex items-start space-x-2"
                  >
                    <MapPin className="w-4 h-4 mt-1 flex-shrink-0 text-blue-600" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{result.display_name.split(',')[0]}</div>
                      <div className="text-xs text-gray-500 truncate">{result.display_name}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {showResults && searchQuery && searchResults.length === 0 && !isSearching && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white text-gray-900 rounded-lg shadow-lg p-3 z-50">
                <p className="text-sm text-gray-500">No results found</p>
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={() => setShowMobileSearch(true)}
            className="p-2 hover:bg-gray-700 rounded-lg"
          >
            <Search className="w-5 h-5" />
          </button>
        )}
      </div>

      <div className={`flex items-center space-x-1 md:space-x-4 ${showMobileSearch ? 'hidden md:flex' : ''}`}>
        <div className="hidden md:flex items-center space-x-2 hover:bg-gray-700 rounded-lg p-2 cursor-pointer">
          <User className="w-5 h-5" />
          <span>Admin</span>
          <ChevronDown className="w-4 h-4" />
        </div>
        <div className="md:hidden">
          <User className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
};

export default Header;
